import React, { Dispatch, FC, SetStateAction, useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import noop from 'lodash/noop';

import { doRequest } from '@r1-frontend/do-request';
import {
    requestByConnection,
    RequestDto,
    ResponseDto,
} from '@r1-frontend/api-domru/api-request/v1/user/request-by-connection';

import { btnTypes } from '@r1-frontend/ui-react/components/buttons/baseButton';
import { PolicyNote } from '@r1-frontend/ui-react/components/PolicyNote';
import { EResultPopupStatus } from '@r1-frontend/ui-react/components/popups/popupResult';
import { ACTIONS, CATEGORIES, EVENTS } from '@r1-frontend/shared/constants/analytics';
import { EProductsId } from '@r1-frontend/shared/types/EProductsId';

import PopupResult from '~/src/components/Popups/PopupResult';
import { TField } from '~/src/components/RequestForm';
import { POLICY_PAGE_LINK } from '~/src/constants';
import { IPopupProps as IPopupPropsExport } from '~/src/features/router/order-card';
import { checkMobilePlank } from '~/src/helpers/checkMobilePlank';
import { dataLayerForCBPhonePush, IDataLayerPushUid } from '~/src/helpers/eventsForDataLayer';
import { TState } from '~/src/store';
import { selectCsrf } from '~/src/store/selectors/auth/selectCsrf';

import * as ST from './styled';

const MOVIX_PRO_VOICE_ID = 101183;
export interface IPopupProps extends IPopupPropsExport {
    leasingDuration?: number,
    costType?: number,
    salePackageId?: number,
}

type TDispatch<T> = Dispatch<SetStateAction<T>>;

interface IProps {
    textButton?: string,
    isLoading: boolean,
    setPopup: TDispatch<IPopupProps>,
    setIsLoading: TDispatch<boolean>,
    popup: IPopupProps,
    label: string,
}

export enum EEquipmentType {
    loan = 0,
    sale = 1,
    leasing = 5,
}

const Popup: FC<IProps> = ({ textButton, setPopup, popup, setIsLoading, isLoading, label }) => {
    const csrf = useSelector(selectCsrf);
    const defaultFieldValue = { value: '', isValid: false, clearInput: () => noop() };
    const [name, setName] = useState<TField>(defaultFieldValue);
    const [phone, setPhone] = useState<TField>(defaultFieldValue);
    const [beenStarted, setDataLayer] = useState<boolean>(false);
    const isAuth = useSelector((state: TState) => state.auth.isAuth);
    const clientPersonal = useSelector((state: TState) => state.auth.clientPersonal);
    const [isShowMobilePlank, setIsShowMobilePlank] = useState(false);

    useEffect(() => {
        setIsShowMobilePlank(checkMobilePlank(isAuth as boolean));
    }, [isAuth]);

    const phoneChangeHandler = useCallback((stateInput: TField, _setState: TDispatch<TField>) => {
        const stateResult = typeof stateInput === 'object' ? stateInput.value : stateInput;
        // @TODO stateInput.value убрать из условия после фикса nameDecorator (useEffect onchange) UI react
        if (!beenStarted && stateResult) {
            dataLayerForCBPhonePush({
                event: EVENTS.UAevent,
                category: CATEGORIES.applicationForm,
                action: isAuth ? ACTIONS.startedVas : ACTIONS.started,
                label,
                uid: isAuth ? clientPersonal?.agreement : null,
            } as unknown as IDataLayerPushUid);
            setDataLayer(true);
        }
        _setState(stateInput);
    }, [beenStarted, label]);

    const openPopupResult = useCallback((result, message) => {
        dataLayerForCBPhonePush({
            action: result ? ACTIONS.finished : ACTIONS.error,
            option: result ? label : `${label} | ${message}`,
            uid: isAuth ? clientPersonal?.agreement : null,
        });
        setDataLayer(false);
        setPopup((state: IPopupProps) => ({
            ...state,
            isOrderOpened: false,
            isResultOpened: true,
            leasingDuration: undefined,
            salePackageId: undefined,
            costType: EEquipmentType.loan,
            resultType: result ? EResultPopupStatus.SUCCESS : EResultPopupStatus.ERROR,
            resultTitle: result ? 'Заявка отправлена' : 'Произошла ошибка',
            resultText: message || (
                result
                    ? 'Мы свяжемся с вами в ближайшее время'
                    : 'Что-то пошло не так, попробуйте позже.'
            ),
        }));
    }, []);

    const closePopup = useCallback(() => {
        setDataLayer(false);
        setPhone({ ...phone, value: '' });
        setPopup((state: IPopupProps) => ({
            ...state,
            isOrderOpened: false,
            isResultOpened: false,
            leasingDuration: undefined,
            salePackageId: undefined,
            costType: EEquipmentType.loan,
        }));
    }, []);

    const onSubmit = async() => {
        setIsLoading(true);

        const params: RequestDto = {
            csrf,
            fio: name.value,
            phone: phone.value,
            cost_type: popup.costType,
            leasing_duration: popup.leasingDuration,
            plan: popup?.salePackageId,
            product_id: [EProductsId.tv],
            tv_equipment_id: [MOVIX_PRO_VOICE_ID],
        };

        const resp = await doRequest.apiRequest<ResponseDto>(requestByConnection(params));
        if (resp.isSuccess) {
            const { status, message } = resp.payload;
            const ok = !!status;

            openPopupResult(ok, message);
        } else {
            openPopupResult(false, 'Не удалось отправить заявку. Пожалуйста, попробуйте еще раз.');
        }
    };

    const buttonText = textButton || 'Отправить заявку';

    return (
        <>
            <ST.Wrapper isOpen={popup.isOrderOpened} onClose={closePopup} isShowMobilePlank={isShowMobilePlank}>
                <ST.Title>Оформление заявки</ST.Title>
                <ST.Subtitle>
                    Оставьте заявку. Мы перезвоним в ближайшее время, ответим на ваши вопросы и поможем оформить заказ.
                </ST.Subtitle>
                <ST.InputBlock>
                    <ST.Name
                        name='name'
                        onChange={(value: TField) => phoneChangeHandler(value, setName)}
                        placeholder='Как вас зовут?'
                        errorMessage='Ожидаются только буквы и дефисы'
                        className={'ym-disable-keys'}
                    />
                    <ST.Phone
                        name='phone'
                        onChange={(phone: TField) => phoneChangeHandler(phone, setPhone)}
                        placeholder={'Номер телефона'}
                        errorMessage={'Введите телефон в формате +7 (999) 999-99-99'}
                        initialValue={phone.value}
                        className={'ym-disable-keys'}
                    />
                </ST.InputBlock>
                <ST.InfoText>
                    <PolicyNote
                        data-test='agreement'
                        buttonText={buttonText}
                        agreeUrl={POLICY_PAGE_LINK}
                    />
                </ST.InfoText>
                <ST.Button
                    type={btnTypes.ACCENT}
                    disabled={!phone.isValid || !name.isValid || isLoading}
                    text={buttonText}
                    onClick={onSubmit}
                />
            </ST.Wrapper>
            <PopupResult
                isOpen={popup.isResultOpened}
                onClose={closePopup}
                title={popup.resultTitle}
                text={popup.resultText}
                type={popup.resultType}
                // @ts-ignore
                style={{ maxWidth: '100%' }}
            />
        </>
    );
};

export default Popup;
